import { shipmentContactInputSchema } from "../shipment-contact.schema";
import { shippingTextSchema } from "@/lib/shipping-text";
/**
 * Bulk-create — per-row validation helpers.
 *
 * Pure, synchronous field validation run before (and independently of) the
 * async geocoding / quote steps. The UI uses these to flag rows that can never
 * produce a quote, and to gate manual entry.
 */
import type { ParsedOrder } from "./types";

/** Fields that can carry a validation issue. */
export type RowField =
  | "recipientName"
  | "recipientAddress"
  | "recipientUnit"
  | "recipientCity"
  | "recipientProvince"
  | "recipientPostalCode"
  | "recipientPhone"
  | "recipientEmail"
  | "weight"
  | "length"
  | "width"
  | "height";

export interface RowValidationIssue {
  field: RowField;
  message: string;
}

/** Canadian postal code: `A1A 1A1` (optional space, case-insensitive). */
export const CA_POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

export const isValidCanadianPostalCode = (value: string): boolean =>
  CA_POSTAL_CODE_REGEX.test(value.trim());

/** Loose E.164-ish check: 10+ digits once non-digits are stripped. */
export const isPlausiblePhone = (value: string): boolean =>
  value.replace(/\D/g, "").length >= 10;

/**
 * Validate a single row's user-supplied fields. Returns an empty array when the
 * row is complete enough to attempt geocoding + quoting. Does NOT assert that
 * the address is real — that is the geocoder's job.
 */
export const validateRow = (order: ParsedOrder): RowValidationIssue[] => {
  const issues: RowValidationIssue[] = [];
  for (const [field, label] of [
    ["recipientName", "Recipient name"],
    ["recipientAddress", "Street address"],
    ["recipientUnit", "Unit"],
    ["recipientCity", "City"],
  ] as const) {
    const result = shippingTextSchema.safeParse(order[field]);
    if (!result.success)
      issues.push({
        field,
        message: `${label}: ${result.error.issues[0].message}`,
      });
  }

  if (!order.recipientName.trim())
    issues.push({ field: "recipientName", message: "Recipient name required" });
  if (!order.recipientAddress.trim())
    issues.push({
      field: "recipientAddress",
      message: "Street address required",
    });
  if (!order.recipientCity.trim())
    issues.push({ field: "recipientCity", message: "City required" });
  if (!order.recipientProvince.trim())
    issues.push({ field: "recipientProvince", message: "Province required" });
  if (!isValidCanadianPostalCode(order.recipientPostalCode))
    issues.push({
      field: "recipientPostalCode",
      message: "Valid postal code required",
    });
  const contact = shipmentContactInputSchema(
    order.validatedAddress?.country ?? "CA"
  ).safeParse({
    name: order.recipientName,
    phone: order.recipientPhone,
    email: order.recipientEmail,
  });
  if (!contact.success)
    for (const issue of contact.error.issues) {
      if (issue.path[0] === "phone" || issue.path[0] === "email")
        issues.push({
          field:
            issue.path[0] === "phone" ? "recipientPhone" : "recipientEmail",
          message: issue.message,
        });
    }

  if (!(order.weight > 0))
    issues.push({ field: "weight", message: "Weight must be greater than 0" });
  if (!(order.length > 0))
    issues.push({ field: "length", message: "Length must be greater than 0" });
  if (!(order.width > 0))
    issues.push({ field: "width", message: "Width must be greater than 0" });
  if (!(order.height > 0))
    issues.push({ field: "height", message: "Height must be greater than 0" });

  return issues;
};

/** True when the row has all fields needed to attempt a quote. */
export const isRowComplete = (order: ParsedOrder): boolean =>
  validateRow(order).length === 0;

/** True when the row is quoted, selected, and ready to be created. */
export const isRowShippable = (order: ParsedOrder): boolean =>
  order.status === "ready" && !!order.selectedQuote && order.selected;

export function updateBulkShippingRow<K extends keyof ParsedOrder>(
  row: ParsedOrder,
  field: K,
  value: ParsedOrder[K]
): ParsedOrder {
  const updated = { ...row, [field]: value };
  if (
    row[field] === value ||
    ![
      "recipientName",
      "recipientAddress",
      "recipientUnit",
      "recipientCity",
      "recipientProvince",
      "recipientPostalCode",
    ].some((key) => key === field)
  )
    return updated;
  const issue = validateRow(updated)[0];
  return {
    ...updated,
    status: "needs_fix",
    selectedQuote: undefined,
    quoteId: undefined,
    availableQuotes: undefined,
    validatedAddress: undefined,
    errorMessage:
      issue?.message ??
      "Shipping details changed. Verify this row again for a new quote.",
  };
}

export function mergeBulkShippingRow(
  current: ParsedOrder[],
  source: ParsedOrder,
  result: ParsedOrder
): ParsedOrder[] {
  const inputFields = [
    "recipientName",
    "recipientAddress",
    "recipientUnit",
    "recipientCity",
    "recipientProvince",
    "recipientPostalCode",
    "recipientPhone",
    "recipientEmail",
    "weight",
    "weightUnit",
    "length",
    "width",
    "height",
    "dimensionUnit",
  ] as const;
  return current.map((row) => {
    if (
      row.id !== source.id ||
      row.id !== result.id ||
      row.status === "processing" ||
      row.status === "completed" ||
      inputFields.some((field) => row[field] !== source[field])
    )
      return row;
    return { ...result, selected: row.selected };
  });
}
